/**
 * GuideBlocks — rendu des blocs d'un guide (p · steps · list · table · tip · warn · example · formula). Batch #14.302.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Lightbulb } from "lucide-react";
import { md } from "./_help";

function Steps({ items, sid }) {
  return (
    <ol className="space-y-2.5" data-testid={`help-steps-${sid}`}>
      {items.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
          <div className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">{s.title}</span>
            {s.text && <span> — {md(s.text)}</span>}
            {s.link && (
              <Link to={s.link.to} className="inline-flex items-center gap-1 ml-2 text-xs font-semibold text-sky-700 hover:underline" data-testid={`help-step-link-${sid}-${i}`}>
                {s.link.label} <ArrowUpRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function DataTable({ columns, rows, sid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid={`help-table-${sid}`}>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-900 text-white text-[11px] uppercase tracking-wide">{columns.map((c) => <th key={c} className="text-left px-3 py-2 font-semibold">{c}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
              {r.map((c, j) => <td key={j} className={`px-3 py-2 align-top text-slate-700 leading-snug ${j === 0 ? "font-semibold text-slate-900" : ""}`}>{md(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ kind, text }) {
  const warn = kind === "warn";
  const Icon = warn ? AlertTriangle : Lightbulb;
  return (
    <div className={`flex gap-3 rounded-xl border-l-4 p-3.5 text-sm leading-relaxed ${warn ? "bg-rose-50 border-rose-500 text-rose-900" : "bg-amber-50 border-amber-500 text-amber-900"}`} data-testid={`help-${kind}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" /><div>{md(text)}</div>
    </div>
  );
}

function Example({ title, rows, sid }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid={`help-example-${sid}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-emerald-800 mb-2">{title || "Exemple"}</div>
      <dl className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(([k, v], i) => (<div key={i} className="contents"><dt className="text-slate-600">{md(k)}</dt><dd className="font-mono text-slate-900">{md(v)}</dd></div>))}
      </dl>
    </div>
  );
}

export function GuideBlock({ block, sid }) {
  switch (block.type) {
    case "p": return <p className="text-sm text-slate-700 leading-relaxed">{md(block.text)}</p>;
    case "steps": return <Steps items={block.items} sid={sid} />;
    case "list": return <ul className="space-y-1.5 text-sm text-slate-700 leading-relaxed list-disc pl-5">{block.items.map((x, i) => <li key={i}>{md(x)}</li>)}</ul>;
    case "table": return <DataTable columns={block.columns} rows={block.rows} sid={sid} />;
    case "tip": return <Callout kind="tip" text={block.text} />;
    case "warn": return <Callout kind="warn" text={block.text} />;
    case "example": return <Example title={block.title} rows={block.rows} sid={sid} />;
    case "formula": return <pre className="whitespace-pre-wrap font-mono text-xs bg-slate-900 text-emerald-200 rounded-xl p-3.5 leading-relaxed" data-testid="help-formula">{block.text}</pre>;
    default: return null;
  }
}
